import { PantallaCargando } from '@/components/Cargando';
import { useState, useEffect } from 'react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { CreditCard, BarChart3, AlertTriangle, TrendingUp, Puzzle, ArrowUpRight, HardDrive, Clock } from 'lucide-react';
import { MODULE_LABELS, LIMIT_LABELS } from '@/lib/planLabels';

export default function MyPlan() {
  const [plan, setPlan] = useState(null);
  const [limits, setLimits] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [usage, setUsage] = useState(null);
  const [storage, setStorage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [pRes, lRes, aRes, uRes] = await Promise.all([
          api.get('/tenant/plan'),
          api.get('/tenant/limits'),
          api.get('/tenant/alerts'),
          api.get('/tenant/usage?period=monthly'),
        ]);
        setPlan(pRes.data);
        setLimits(lRes.data);
        setAlerts(aRes.data);
        setUsage(uRes.data);
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
      finally { setLoading(false); }
      // Almacenamiento — pantalla propia, no bloquea el resto de la página
      // si falla (ej. entitlement storage_enabled en false).
      try {
        const { data } = await api.get('/tenant/storage/usage');
        setStorage(data);
      } catch { /* silencioso — panel simplemente no aparece */ }
    };
    fetchData();
  }, []);

  if (loading) return <PantallaCargando />;

  const limitsData = limits?.limits || {};
  const usageByType = usage?.by_type || {};

  const fmtBytes = (n) => {
    if (!n || n <= 0) return '0 MB';
    const gb = n / (1024 ** 3);
    if (gb >= 1) return `${gb.toFixed(2)} GB`;
    return `${(n / (1024 ** 2)).toFixed(1)} MB`;
  };

  return (
    <div data-testid="my-plan-page">
      <h1 className="text-2xl font-bold text-[#0F172A] tracking-tight mb-6">Mi Plan</h1>

      {/* Plan Card */}
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-[#0F172A] flex items-center justify-center">
            <CreditCard className="w-5 h-5 text-white" />
          </div>
          <div>
            {plan?.has_plan ? (
              <>
                <h2 className="text-lg font-bold text-[#0F172A]" data-testid="plan-name">{plan.plan.name}</h2>
                <p className="text-xs text-[#64748B]">{plan.plan.description}</p>
              </>
            ) : (
              <>
                <h2 className="text-lg font-bold text-[#0F172A]">Sin Plan</h2>
                <p className="text-xs text-[#64748B]">Contacta al administrador para asignar un plan</p>
              </>
            )}
          </div>
        </div>
        {plan?.subscription && (
          <div className="flex flex-wrap gap-4 text-xs text-[#64748B] border-t border-[#F1F5F9] pt-3">
            <span>Estado: <strong className="text-[#0F172A]">{plan.subscription.status}</strong></span>
            <span>Ciclo: <strong className="text-[#0F172A]">{plan.subscription.billing_cycle}</strong></span>
            <span>Pago: <strong className="text-[#0F172A]">{plan.subscription.payment_status}</strong></span>
            {plan.subscription.renewal_date && <span>Renovación: <strong className="text-[#0F172A]">{new Date(plan.subscription.renewal_date).toLocaleDateString()}</strong></span>}
            {plan.subscription.trial_end && <span className="text-blue-500">Trial hasta: {new Date(plan.subscription.trial_end).toLocaleDateString()}</span>}
          </div>
        )}
        {plan?.has_plan && plan.plan.included_modules?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {plan.plan.included_modules.map(m => <span key={m} className="px-2 py-0.5 bg-[#F1F5F9] text-[#475569] text-[11px] rounded">{MODULE_LABELS[m] || m}</span>)}
          </div>
        )}
        {plan?.addons?.length > 0 && (
          <div className="mt-3 pt-3 border-t border-[#F1F5F9]">
            <p className="text-xs font-medium text-[#64748B] mb-1.5 flex items-center gap-1"><Puzzle className="w-3 h-3" /> Add-ons</p>
            <div className="flex flex-wrap gap-1.5">
              {plan.addons.map(a => <span key={a.id} className="px-2 py-0.5 bg-[#F72585]/5 text-[#F72585] text-[11px] rounded">{a.name} (x{a.quantity})</span>)}
            </div>
          </div>
        )}
      </div>

      {/* Limits */}
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 mb-6">
        <h3 className="text-sm font-semibold text-[#0F172A] flex items-center gap-2 mb-4"><BarChart3 className="w-4 h-4" /> Mis Límites</h3>
        <div className="space-y-3">
          {Object.entries(limitsData).map(([key, info]) => {
            const limit = typeof info === 'object' ? info.limit : info;
            const used = typeof info === 'object' ? info.used : 0;
            const pct = limit > 0 ? Math.min(100, (used / limit) * 100) : 0;
            const barColor = pct >= 100 ? 'bg-red-500' : pct >= 75 ? 'bg-amber-500' : 'bg-emerald-500';
            return (
              <div key={key} data-testid={`limit-${key}`}>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-[#475569]">{LIMIT_LABELS[key] || key.replace(/max_|_monthly/g, '').replace(/_/g, ' ')}</span>
                  <span className="text-[#0F172A] font-medium">{used} / {limit}</span>
                </div>
                <div className="h-1.5 bg-[#F1F5F9] rounded-full overflow-hidden">
                  <div className={`h-full rounded-full transition-all ${barColor}`} style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
          {Object.keys(limitsData).length === 0 && <p className="text-xs text-[#94A3B8] text-center py-2">Sin límites definidos</p>}
        </div>
      </div>

      {/* Almacenamiento — R2/local, cuenta documentos de Conocimiento y
          medios de Publicaciones subidos desde hoy en adelante (los
          archivos guardados antes de que esto se conectara no se cuentan
          todavía; ver nota del administrador). */}
      {storage && (
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 mb-6" data-testid="storage-usage-card">
          <h3 className="text-sm font-semibold text-[#0F172A] flex items-center gap-2 mb-4">
            <HardDrive className="w-4 h-4" /> Almacenamiento
          </h3>
          {!storage.storage_enabled ? (
            <p className="text-xs text-[#94A3B8]">Almacenamiento no habilitado en tu plan.</p>
          ) : (
            <>
              <div className="flex justify-between text-xs mb-1">
                <span className="text-[#475569]">Usado</span>
                <span className="text-[#0F172A] font-medium">
                  {fmtBytes(storage.used_bytes)} / {fmtBytes(storage.limit_bytes)} ({storage.usage_percent}%)
                </span>
              </div>
              <div className="h-1.5 bg-[#F1F5F9] rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${storage.usage_percent >= 100 ? 'bg-red-500' : storage.usage_percent >= 75 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                  style={{ width: `${Math.min(100, storage.usage_percent)}%` }}
                />
              </div>
              {storage.uploads_blocked && (
                <p className="text-[11px] text-red-600 mt-2">Límite alcanzado — no se pueden subir más archivos hasta liberar espacio o ampliar el plan.</p>
              )}
              <p className="text-[10px] text-[#94A3B8] mt-2">
                Documentos de Conocimiento y medios de Publicaciones. Solo cuenta lo subido desde que este contador se activó.
              </p>
            </>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Usage */}
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-5">
          <h3 className="text-sm font-semibold text-[#0F172A] flex items-center gap-2 mb-4"><TrendingUp className="w-4 h-4" /> Consumo del Mes</h3>
          {Object.keys(usageByType).length === 0 ? (
            <p className="text-xs text-[#94A3B8] text-center py-4">Sin consumo registrado</p>
          ) : (
            <div className="space-y-2">
              {Object.entries(usageByType).map(([type, count]) => (
                <div key={type} className="flex items-center justify-between">
                  <span className="text-xs text-[#475569]">{type.replace(/_/g, ' ')}</span>
                  <span className="text-xs font-medium text-[#0F172A] bg-[#F1F5F9] px-2 py-0.5 rounded">{count}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Alerts */}
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-5">
          <h3 className="text-sm font-semibold text-[#0F172A] flex items-center gap-2 mb-4"><AlertTriangle className="w-4 h-4" /> Alertas</h3>
          {alerts.length === 0 ? (
            <p className="text-xs text-[#94A3B8] text-center py-4">Sin alertas activas</p>
          ) : (
            <div className="space-y-2">
              {alerts.map(a => (
                <div key={a.id} className="flex items-center gap-3 py-2 border-b border-[#F8F9FA] last:border-0">
                  <AlertTriangle className={`w-4 h-4 flex-shrink-0 ${a.threshold >= 100 ? 'text-red-500' : 'text-amber-500'}`} />
                  <div>
                    <p className="text-xs font-medium text-[#0F172A]">{LIMIT_LABELS[a.limit_key] || a.limit_key.replace(/_/g, ' ')}</p>
                    <p className="text-[11px] text-[#94A3B8]">{a.current_usage}/{a.limit_value} ({a.threshold}%)</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Upgrade placeholder */}
      <MiTarjeta />

      <div className="mt-6 bg-gradient-to-r from-[#0F172A] to-[#1E293B] rounded-xl p-5 text-white" data-testid="upgrade-placeholder">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold">Necesitas más capacidad?</h3>
            <p className="text-xs text-slate-300 mt-1">Contacta al administrador para upgrader tu plan o agregar add-ons.</p>
          </div>
          <ArrowUpRight className="w-5 h-5 text-[#F72585]" />
        </div>
      </div>
    </div>
  );
}


/** El cobro del cliente: cuanto y cuando le toca, y su tarjeta guardada.
 *
 *  La tarjeta la captura la pagina segura de Stripe, no esta pantalla. Aqui
 *  nunca pasa un numero de tarjeta: solo se ve la marca y los ultimos cuatro
 *  digitos, que es lo unico que sirve para reconocerla.
 */
function MiTarjeta() {
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [ocupado, setOcupado] = useState(false);

  const cargar = async () => {
    try {
      const { data } = await api.get('/tenant/mi-cobro');
      setDatos(data);
    } catch (e) {
      // Si el negocio no tiene cobro configurado, esta seccion simplemente no
      // se pinta. No es un error que el cliente deba ver.
      setDatos(null);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargar();
    // Al volver de la pagina de Stripe, la direccion trae ?tarjeta=guardada.
    // Se le pregunta a Stripe cual quedo — nunca se confia en el parametro.
    const params = new URLSearchParams(window.location.search);
    if (params.get('tarjeta') === 'guardada') {
      api.post('/tenant/mi-cobro/tarjeta/confirmar')
        .then(({ data }) => {
          if (data.guardada) toast.success(`Tarjeta ${data.marca} ****${data.ultimos4} guardada`);
          else toast.info('Todavia no vemos la tarjeta. Vuelve a intentar en un momento.');
          cargar();
        })
        .catch(() => {});
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const guardar = async () => {
    setOcupado(true);
    try {
      const { data } = await api.post('/tenant/mi-cobro/tarjeta/enlace');
      window.location.href = data.enlace;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setOcupado(false);
    }
  };

  const quitar = async () => {
    if (!window.confirm('Al quitar tu tarjeta dejaremos de cobrarte automaticamente. Continuar?')) return;
    setOcupado(true);
    try {
      await api.delete('/tenant/mi-cobro/tarjeta');
      toast.success('Tarjeta retirada');
      await cargar();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setOcupado(false);
    }
  };

  if (cargando || !datos) return null;
  const p = datos.proximo_cobro || {};
  const t = datos.tarjeta;

  return (
    <div className="mt-6 rounded-xl border border-[#E2E8F0] dark:border-white/10 p-5" data-testid="mi-tarjeta">
      <div className="flex items-center gap-2 text-sm font-semibold mb-1">
        <CreditCard className="w-4 h-4" /> Tu pago
      </div>

      {/* La prueba gratis: se le recuerda guardar su tarjeta, sin amenazar.
          Durante la prueba no se le cobra nada y no se le quita nada. */}
      {datos.prueba && (
        <div className={`rounded-lg border p-4 mb-4 ${datos.prueba.urgente
          ? 'border-amber-300 bg-amber-50 dark:bg-amber-500/10'
          : 'border-sky-200 bg-sky-50 dark:bg-sky-500/10'}`}
             data-testid="aviso-prueba">
          <div className="flex items-start gap-2">
            <Clock className={`w-4 h-4 mt-0.5 shrink-0 ${
              datos.prueba.urgente ? 'text-amber-600' : 'text-sky-600'}`} />
            <div>
              <p className="text-sm font-semibold">{datos.prueba.titulo}</p>
              <p className="text-xs text-[#64748B] mt-1">{datos.prueba.texto}</p>
            </div>
          </div>
        </div>
      )}

      {/* Si su servicio esta pausado, se le dice de frente y en su propia
          pantalla. Enterarse porque «algo dejo de funcionar» es la peor forma
          de descubrirlo, y la que genera la llamada enojada. */}
      {datos.cobranza && (
        <div className={`rounded-lg border p-4 mb-4 ${
          datos.cobranza.estado === 'suspendido'
            ? 'border-red-300 bg-red-50 dark:bg-red-500/10'
            : 'border-amber-300 bg-amber-50 dark:bg-amber-500/10'}`}
             data-testid="aviso-cobranza">
          <div className="flex items-start gap-2">
            <AlertTriangle className={`w-4 h-4 mt-0.5 shrink-0 ${
              datos.cobranza.estado === 'suspendido' ? 'text-red-600' : 'text-amber-600'}`} />
            <div>
              <p className="text-sm font-semibold">{datos.cobranza.titulo}</p>
              <p className="text-xs text-[#64748B] mt-1">{datos.cobranza.texto}</p>
            </div>
          </div>
        </div>
      )}
      <p className="text-xs text-[#64748B] mb-4">
        Te cobra <strong>{datos.te_cobra}</strong> y recibes {datos.documento === 'factura' ? 'una factura' : 'un invoice'}.
      </p>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-lg bg-[#F8F9FA] dark:bg-white/5 p-4">
          <div className="text-xs text-[#64748B] mb-1">Tu proximo pago</div>
          <div className="text-2xl font-bold tabular-nums">
            {p.monto} <span className="text-sm font-normal text-[#64748B]">{p.moneda}</span>
          </div>
          <div className="text-xs text-[#64748B] mt-1">
            {p.fecha ? `El ${p.fecha}, ${p.periodicidad}` : 'Sin fecha de cobro asignada'}
          </div>
          {(p.desglose || []).length > 1 && (
            <ul className="mt-3 space-y-1">
              {p.desglose.map((d, i) => (
                <li key={i} className="flex justify-between text-xs text-[#64748B]">
                  <span>{d.concepto}</span><span className="tabular-nums">{d.monto}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="rounded-lg bg-[#F8F9FA] dark:bg-white/5 p-4">
          <div className="text-xs text-[#64748B] mb-1">Tu tarjeta</div>
          {t ? (
            <>
              <div className="text-lg font-semibold">
                {(t.marca || '').toUpperCase()} ****{t.ultimos4}
              </div>
              <div className="text-xs text-[#64748B] mt-1">
                Vence {String(t.vence_mes).padStart(2, '0')}/{t.vence_anio}
              </div>
              <div className="flex gap-2 mt-3">
                <button onClick={guardar} disabled={ocupado}
                        className="text-xs px-3 py-1.5 rounded-md border border-[#E2E8F0] dark:border-white/10 hover:bg-white/50 disabled:opacity-50">
                  Cambiar tarjeta
                </button>
                <button onClick={quitar} disabled={ocupado}
                        className="text-xs px-3 py-1.5 rounded-md text-red-600 hover:bg-red-50 disabled:opacity-50">
                  Quitar
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="text-xs text-[#64748B] mb-3">
                No tienes ninguna guardada. Mientras no la pongas, no te cobramos nada
                automaticamente.
              </p>
              <button onClick={guardar} disabled={ocupado}
                      className="text-xs px-3 py-1.5 rounded-md bg-[#0F172A] text-white hover:opacity-90 disabled:opacity-50"
                      data-testid="guardar-tarjeta">
                {ocupado ? 'Abriendo...' : 'Guardar mi tarjeta'}
              </button>
              <p className="text-[11px] text-[#64748B] mt-2">
                Se abre la pagina segura de Stripe. Tu numero de tarjeta nunca pasa por
                nuestros servidores.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
